import React, { useCallback, useContext, useState } from "react";
import { MWebLicense, MWebLoadingState } from "./useMWeb";
import Frame from "../common/helperfunctions/Frame";
import { Dataset } from "../api/Datasets";
import { SessionContext } from "../common/contexts/SessionContext";
import BrandIcon from "../common/loaders/Spinner/BrandIcon";
import { showtoast } from "../common/overlays/Toasts/showtoast";
import { useQueryClient } from "@tanstack/react-query";
import { Id, toast } from "react-toastify";
import { formatBytes } from "../Dataset/common/Tree/Folder/helpers/FileHelpers";

const qtLoaderScriptId = "mweb-qtloader" as const; // Unique ID for the script
const mwebIntegrationScriptId = "mweb-integration" as const; // Unique ID for the script

interface MNovaFile {
  name: string;
  data: Uint8Array;
}

export interface MWebContainerProps {
  datasetId: Dataset["id"];
  mNovaFile: string;
  license: MWebLicense;
  MWebFinished: boolean;
  MWebLoadingStatus?: MWebLoadingState;
  MWebError?: string;
  MWebFetchFunc: (mNovaFile: string) => Promise<MNovaFile>;
  MWebFileSaveFunc?: (name: string, data: Uint8Array) => void;
  mwebResourcePath: string; // Without trailing slash
  style: React.CSSProperties;
  canvasId?: string;
  permissions: Dataset["permissions"];
  viewShared: boolean;
  // Narrow down the types later here once we understand what types are actually passed
  // in those functions

  // The status messages I've seen are "Downloading" and "Downloading/Compiling"
  MWebloadingFunc?: (status: MWebLoadingState) => void;
  MWebErrorCallback?: (errorText: any) => void;
  showExit?: () => void;

  // This callback seems to be triggered once the loading is done
  // In the example it is used to hide the loading spinner
  MWebFinishedCallback?: () => void;
}

export const MWebContainer = ({
  datasetId,
  mNovaFile,
  license,
  MWebFinished,
  MWebLoadingStatus,
  MWebError,
  MWebFetchFunc,
  MWebFileSaveFunc,
  mwebResourcePath,
  canvasId,
  style,
  MWebloadingFunc,
  MWebErrorCallback,
  showExit,
  MWebFinishedCallback,
  permissions,
  viewShared,
}: MWebContainerProps) => {
  // const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // var canvas = document.querySelector("#qtcanvas");
  // console.log("MWebContainer", license != null);
  const { api, session, group } = useContext(SessionContext);
  const queryClient = useQueryClient();
  const params = new URLSearchParams(window.location.search);
  const datasetVersion = params.get("datasetVersion");
  const [frameKey, setFrameKey] = useState(datasetVersion);
  const toastId = React.useRef<Id | null>(null) as React.MutableRefObject<Id | null>;
  const [isUpdating, setIsUpdating] = useState(false);
  const notify = useCallback((message: string) => {
    toastId.current = toast(message, { type: "info", autoClose: false, progress: 0 });
  }, []);
  const [downloadProgress, setDownloadProgress] = useState<{
    bytesLoaded: number;
    totalBytes: number;
    progress: number;
  }>();

  const onDownloadCallback = useCallback((bytesLoaded: number, totalBytes: number) => {
    setDownloadProgress({ bytesLoaded, totalBytes, progress: (bytesLoaded / totalBytes) * 100 });
  }, []);

  const update = useCallback(
    (message: string, progress: number, autoClose?: number) => {
      if (toastId.current)
        toast.update(toastId.current, { render: message, type: autoClose ? "success" : "info", progress, autoClose });
    },
    [toastId]
  );

  React.useEffect(() => {
    setFrameKey(datasetVersion);
    setQtLoaded(false);
  }, [datasetVersion]);

  // const onSaveCallback = useCallback(
  //   async (name: string, bytes: number) => {
  //     await queryClient.invalidateQueries([`datasets/${datasetId}/files/versions`]);
  //     showtoast("success", `Saved ${name} ${formatBytes(bytes)}`);
  //   },
  //   [datasetId, queryClient]
  // );

  const onProgressCallback = useCallback(
    async (bytesLoaded: number, totalBytes: number, name: string) => {
      setIsUpdating(true);
      const progress = bytesLoaded / totalBytes;
      if (!toastId.current) {
        notify(`Saving ${name}...`);
      } else {
        update(`Saving ${name} (${formatBytes(bytesLoaded)}/${formatBytes(totalBytes)})`, progress);
      }
      if (progress === 1) {
        update(`Saved ${name} (${formatBytes(totalBytes)})`, progress, 3000);
        await queryClient.invalidateQueries([`datasets/${datasetId}/files/versions`]);
        setTimeout(() => {
          !!toastId.current && toast.dismiss(toastId.current);
          toastId.current = null;
        }, 2000);
        setIsUpdating(false);
      }
    },
    [datasetId, notify, queryClient, update]
  );

  const onErrorCallback = useCallback((error: string) => {
    showtoast("error", error);
  }, []);

  const onLoad = useCallback(
    async (iFrame: Document) => {
      var config: any = {
        canvasElements: [iFrame.getElementById("qtcanvas")],
      };
      config.disabledFeatures = ["mweb.base.open*"];
      if (!permissions?.canEdit || viewShared) {
        config.disabledFeatures = [...config.disabledFeatures, "mweb.base.save*", "mweb.page.delete"];
      }

      if (MWebloadingFunc !== undefined) config.showLoader = MWebloadingFunc;
      if (MWebErrorCallback !== undefined) config.showError = MWebErrorCallback;
      if (showExit !== undefined) config.showExit = showExit;
      if (MWebFinishedCallback !== undefined) config.showCanvas = MWebFinishedCallback;

      // Run the initConfig function from the mweb-integration script and add some overrides
      // to the default config
      if ((iFrame as any).initConfig === undefined) {
        window.location.reload();
      }

      await (iFrame as any).initConfig?.(config, {
        onErrorCallback,
        onDownloadCallback,
        onProgressCallback,
      });
    },
    [
      permissions?.canEdit,
      viewShared,
      MWebloadingFunc,
      MWebErrorCallback,
      showExit,
      MWebFinishedCallback,
      onErrorCallback,
      onDownloadCallback,
      onProgressCallback,
    ]
  );

  const [qtLoaded, setQtLoaded] = useState(false);

  const injectScripts = useCallback(
    (iFrame: Document) => {
      if (!iFrame.getElementById(mwebIntegrationScriptId)) {
        console.log("Injecting MWeb integration");
        const integrationScript = iFrame.createElement("script");
        integrationScript.src = `/mweb-integration/mweb-integration.js`;
        integrationScript.async = false;
        integrationScript.id = mwebIntegrationScriptId;
        iFrame.body.appendChild(integrationScript);

        // We pass through keydown events to the canvas
        document.addEventListener("keydown", (e) => {
          const canvas = iFrame.getElementById("qtcanvas");
          if (canvas) {
            const event = new KeyboardEvent("keydown", {
              key: e.key,
              code: e.code,
              keyCode: e.keyCode,
              bubbles: false,
            });
            canvas.dispatchEvent(event);
          }
        });

        document.addEventListener("keyup", (e) => {
          const canvas = iFrame.getElementById("qtcanvas");
          if (canvas) {
            const event = new KeyboardEvent("keyup", { key: e.key, code: e.code, keyCode: e.keyCode, bubbles: false });
            canvas.dispatchEvent(event);
          }
        });

        // We pass through keydown events to the canvas
        iFrame.addEventListener("keydown", (e) => {
          const canvas = iFrame.getElementById("qtcanvas");
          if (canvas) {
            const event = new KeyboardEvent("keydown", {
              key: e.key,
              code: e.code,
              keyCode: e.keyCode,
              bubbles: false,
            });
            canvas.dispatchEvent(event);
          }
        });

        iFrame.addEventListener("keyup", (e) => {
          const canvas = iFrame.getElementById("qtcanvas");
          if (canvas) {
            const event = new KeyboardEvent("keyup", { key: e.key, code: e.code, keyCode: e.keyCode, bubbles: false });
            canvas.dispatchEvent(event);
          }
        });
      }

      if (!iFrame.getElementById(qtLoaderScriptId)) {
        console.log("Injecting QtLoader");
        const script = iFrame.createElement("script");
        script.src = `${mwebResourcePath}/qtloader.js`;
        // script.setAttribute("type", "module");
        script.async = true;
        script.id = qtLoaderScriptId;
        script.onload = async () => await onLoad(iFrame);
        iFrame.body.appendChild(script);
      }

      setQtLoaded(true);
    },
    [mwebResourcePath, onLoad]
  );

  const combinedProgress = ((MWebLoadingStatus?.progress ?? 0) + (downloadProgress?.progress ?? 0)) / 200;

  return (
    <Frame
      key={frameKey}
      style={{
        display: "flex",
        width: "100%",
        height: "100%",
        alignItems: "center",
        justifyContent: "center",
        font: "var(--font-regular)",
        border: 0,
        margin: 0,
        ...style,
      }}
      title="mWebIFrame"
      afterInit={injectScripts}
      customScript={`
    var sessionId = '${session?.sessionId}';
    var authType = '${api.auth.type}';
    var authValue = '${api.auth.value}';
    var authPassword = '${api.auth.password}';
    var group = '${group}';
    var filePath = '${mNovaFile}';
    var datasetId = '${datasetId}';
    var datasetVersion = '${datasetVersion ?? ""}';
    var user = '${session?.person?.name ?? "Anonymous"}';
    var userId = '${session?.person?.id ?? "0"}';
    var proxy = '${globalThis.app.proxy}';
        `}
    >
      <div
        style={{
          display: "flex",
          flexFlow: "column nowrap",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "100%",
        }}
      >
        {!qtLoaded ? (
          <span>Loading...</span>
        ) : (
          combinedProgress !== 1 && (
            <div
              style={{
                display: "flex",
                flexFlow: "column nowrap",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                height: "100%",
              }}
            >
              <BrandIcon
                size={"200px"}
                style={{
                  maskImage: `linear-gradient(to top, black ${combinedProgress * 100}%, transparent ${
                    combinedProgress * 100
                  }%)`,
                  WebkitMaskImage: `linear-gradient(to top, black ${combinedProgress * 100}%, transparent ${
                    combinedProgress * 100
                  }%)`,
                  opacity: combinedProgress,
                }}
              />

              <span style={{ color: "var(--gray-400)" }}>
                {downloadProgress?.bytesLoaded && downloadProgress?.totalBytes
                  ? `${formatBytes(downloadProgress.bytesLoaded)} / ${formatBytes(
                      downloadProgress.totalBytes
                    )} bytes loaded`
                  : MWebLoadingStatus?.message ?? "Loading MWeb"}
              </span>
            </div>
          )
        )}
        <canvas
          id={"qtcanvas"}
          style={{
            ...style,
            ...((!MWebFinished || combinedProgress !== 1) && { width: 0, height: 0 }),
            ...(isUpdating && {
              pointerEvents: "none",
              filter: "blur(2px)",
            }),
          }}
        />
      </div>
    </Frame>
  );
};

// /**
//  * Load the QtLoader script as a script tag and call the onload function when it's loaded
//  *
//  * For some reason the QtLoader script doesn't work when loaded as a module in our
//  * environment.
//  *
//  * @param mwebResourcePath
//  * @param onload
//  */
// const useQtLoaderScript = (mwebResourcePath: string, onload: () => any) => {
//   const [qtLoaded, setQtLoaded] = useState(false);

//   useEffect(() => {
//     // Check if the script is already present
//     if (!document.getElementById(qtLoaderScriptId)) {
//       // console.log("Loading QtLoader");
//       const script = document.createElement("script");
//       script.src = `${mwebResourcePath}/qtloader.js`;
//       // script.setAttribute("type", "module");
//       script.async = true;
//       script.id = qtLoaderScriptId;
//       script.onload = onload;
//       document.body.appendChild(script);
//     }
//     setQtLoaded(true);

//     // Cleanup function to remove the script if the component is unmounted
//     return () => {
//       const script = document.getElementById(qtLoaderScriptId);
//       if (script) {
//         document.body.removeChild(script);
//         // This was an experiment to try and remove the QtLoader objects from the global scope
//         /*eval(`// Define the custom ExitStatus exception
//           function ExitStatus(message) {
//               this.name = "ExitStatus";
//               this.message = message || "Default Message";
//           }
//           ExitStatus.prototype = new Error();
//           ExitStatus.prototype.constructor = ExitStatus;

//           Module.pauseMainLoop();
//           // Call the Module.quit function with code 0 and the ExitStatus exception
//           Module.quit(0, new ExitStatus("Normal exit"));
//           for (var prop in Module) {
//             if (Module.hasOwnProperty(prop)) {
//               delete Module[prop];
//             }
//           }
//           Module = {};`);*/
//       }
//     };
//   }, [mwebResourcePath, onload]);

//   return qtLoaded;
// };
